export function calculateAgeToday(dob) {
  if (!dob) {
    return '';
  }
  const currentDate = new Date();
  const birthDate = new Date(dob);
  if (isNaN(birthDate)) {
    return '';
  }

  let age = currentDate.getFullYear() - birthDate.getFullYear();
  if (
    currentDate.getMonth() < birthDate.getMonth() ||
    (currentDate.getMonth() === birthDate.getMonth() && currentDate.getDate() < birthDate.getDate())
  ) {
    age--;
  }
  return age;
}

export const versionExtract = (illustrationData) => {
  if (typeof illustrationData !== 'object') {
    const conversionString = illustrationData?.replace(/\\s+/g);
    const convertedInJSON = conversionString ? JSON.parse(conversionString) : {};
    return convertedInJSON;
  }
  return illustrationData;
};

export const csvStringToBlob = async (csvString) => {
  const blob = new Blob([csvString], { type: 'text/csv' });
  return blob;
};

export const base64ToBlob = async (base64Data, mimeType) => {
  const removedQuote = base64Data.replace(/"/g, '');
  const binary = atob(removedQuote);
  const length = binary.length;
  const bytes = new Uint8Array(length);
  for (let i = 0; i < length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], mimeType);
  return blob;
};

export const byteArrayToBlob = async (byteArray, mimeType) => {
  const blob = new Blob([byteArray], mimeType);
  return blob;
};

export const streamDataToPdf = async (streamData, mimeType) => {
  const blob = new Blob([streamData], { type: 'application/pdf' });
  return blob;
};

export const blobToDownloadableUrl = (blob) => {
  return URL.createObjectURL(blob);
};

export const downloadPdfFile = (url, fileName) => {
  const aElement = document.createElement('a');
  aElement.href = url;
  aElement.download = `version-${new Date().getTime()}.pdf`;
  document.body.appendChild(aElement);
  aElement.click();
  document.body.removeChild(aElement);
};

export const downloadFile = (url, fileName, ext) => {
  const aElement = document.createElement('a');
  aElement.href = url;
  aElement.download = `${fileName}.${ext}`;
  document.body.appendChild(aElement);
  aElement.click();
  document.body.removeChild(aElement);
};

export function getFileExtension(filename) {
  return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2);
}

export const downloadFileNoExtension = (url, fileName) => {
  const aElement = document.createElement('a');
  aElement.href = url;
  aElement.download = `${fileName}`;
  document.body.appendChild(aElement);
  aElement.click();
  document.body.removeChild(aElement);
};

export function formatByteArraySize(byteArray) {
  const bytes = byteArray.length || byteArray.byteLength || 0;

  if (bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + units[i];
}

export const convertNumberIntoCurrency = (value) => {
  const stringToNumber = Number(value);
  return isNaN(stringToNumber)
    ? 'Invalid number'
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(Number(value));
};

export function isBase64(data) {
  // Check if it's a string
  if (typeof data === 'string') {
    // Base64 regex pattern (allows optional padding)
    const base64Pattern = /^[A-Za-z0-9+/]*={0,2}$/;
    return base64Pattern.test(data);
  }
  return false;
}

export function isByteArray(data) {
  return data instanceof Uint8Array || data instanceof ArrayBuffer || ArrayBuffer.isView(data);
}

export function detectFileDataType(data) {
  if (isByteArray(data)) {
    return 'byteArray';
  } else if (typeof data === 'string') {
    return 'base64';
  }
  return 'unknown';
}

// Checks if base path or sub-paths are active
export const isPathActive = (currentPath, basePath) => {
  return currentPath === basePath || currentPath.startsWith(`${basePath}/`);
};

export const isPathArrayActive = (currentPath, basePaths) => {
  return basePaths.some(
    (basePath) => currentPath === basePath || currentPath.startsWith(`${basePath}/`)
  );
};

// Takes in an ISO string "yyyy-mm-ddT00:00:00.000Z" and format e.g. 'en-us' and returns
// a readable data format yyyy-mm-dd
export const convertIsoToReadableDate = (isoString, format) => {
  const date = new Date(isoString);
  return date.toLocaleDateString(format);
};

// Takes in an ISO string "yyyy-mm-ddT00:00:00.000Z" and format e.g. 'en-us' and returns
// a readable time format hh:mm AM/PM
export const convertIsoToReadableTime = (isoString, options) => {
  const date = new Date(isoString);
  return date.toLocaleTimeString(options);
};

export const toTitleCase = (string) => {
  return string
    .trim()
    .toLowerCase()
    .split(/\s+/) // Handles multiple spaces
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const moveObjToTopOfArr = (arr, predicate) => {
  const match = arr.find(predicate);
  if (!match) return arr;
  return [match, ...arr.filter((item) => item !== match)];
};

export const createGuid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Normalizes a name string by removing commas and extra spaces
 * @param {string} name - The name to normalize
 * @returns {string} - Normalized name with words sorted alphabetically
 */
const normalizeName = (name) =>
  !name || typeof name !== 'string'
    ? ''
    : name
        .split(/[,\s]+/)
        .filter(Boolean)
        .map((part) => part.trim().toLowerCase())
        .sort()
        .join(' ');

/**
 * Compares two name strings regardless of their format
 * @param {string} name1 - First name to compare
 * @param {string} name2 - Second name to compare
 * @returns {boolean} - True if names match
 */
export const compareNames = (name1, name2) => normalizeName(name1) === normalizeName(name2);

/**
 * Decide file-lock ownership by the STABLE blcUserId (extension_blcuserid ⇄
 * bli.User.Id), never the mutable/non-unique display name. Prefers an id-vs-id match
 * when the row carries the lock owner's id (`fileLockedByUserId`); falls back to the
 * legacy name compare only until the EHC SecureFileUploader → IntegrationsHub lock store
 * is re-keyed to persist + return the owner's blcUserId. Display name stays a label only.
 * @param {object} row - grid row (fileLockedByUserId? + fileLockedBy)
 * @param {object} user - current user (blcUserId + displayName)
 * @returns {boolean} true if the current user owns the lock
 */
export const isFileLockOwner = (row, user) => {
  const ownerId = row?.fileLockedByUserId ?? row?.fileLockedByBlcUserId;
  if (ownerId != null && ownerId !== '' && user?.blcUserId != null && user.blcUserId !== '') {
    return String(ownerId) === String(user.blcUserId);
  }
  // Fallback (pre-backend-re-key): match on display name.
  return compareNames(row?.fileLockedBy, user?.displayName);
};

export const capitalizeFirstLetter = (string) => {
  if (!string) return string;
  return string.replace(/^./, (char) => char.toUpperCase());
};
